import React, { useState, useEffect } from 'react';
import { Target, Users, AlertTriangle, Calendar, Trophy, Star, Award, TrendingUp, Activity, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

// Game mode display name mapping (not translated, always English)
const getGameModeDisplay = (gameMode) => {
  const modeMap = {
    'teamDeathmatch': 'Team Deathmatch',
    'domination': 'Domination',
    'searchAndDestroy': 'Search & Destroy',
    'hardpoint': 'Hardpoint',
    'killConfirmed': 'Kill Confirmed',
    'captureTheFlag': 'Capture The Flag',
    'frontline': 'Frontline',
    'freeForAll': 'Free For All',
    'gunGame': 'Gun Game',
    'sticksAndStones': 'Sticks & Stones',
    'oneShotOneKill': 'One Shot One Kill',
    'cranked': 'Cranked',
    'rapidFire': 'Rapid Fire',
    'attackOfTheUndead': 'Attack of the Undead',
    'battleRoyale': 'Battle Royale',
    'quickSolo': 'Quick Solo',
    'searchAndRescue': 'Search & Rescue',
    'multiplayer': 'Multiplayer'
  };
  return modeMap[gameMode] || gameMode;
};

const MatchHistory = ({ user, t }) => {
  const [matchHistory, setMatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({
    totalMatches: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    totalPrizeWon: 0,
    averagePrize: 0
  });

  const MATCHES_PER_PAGE = 10;

  useEffect(() => {
    const loadMatchHistory = async () => {
      if (!user?._id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('Loading match history for user:', user._id);
        
        const response = await axios.get('/api/tournaments', {
          params: {
            status: 'completed',
            limit: 500
          }
        });
        
        let allTournaments = response.data;
        console.log('All tournaments response:', allTournaments);
        
        if (!Array.isArray(allTournaments)) {
          console.error('API response is not an array:', allTournaments);
          allTournaments = [];
        }
        
        // Filter matches where user participated
        const userMatches = allTournaments.filter(match => {
          const isCompleted = match.status === 'completed' || 
                              match.completedAt || 
                              (match.winners && match.winners.length > 0);
          
          if (!isCompleted) return false;
          
          let userParticipated = false;
          
          // Check participants array
          if (match.participants && Array.isArray(match.participants)) {
            userParticipated = match.participants.some(participant => {
              const participantId = typeof participant === 'string' ? participant : participant?._id || participant?.id;
              return participantId === user._id;
            });
          }
          
          // Check if user is in winners
          if (!userParticipated && match.winners && Array.isArray(match.winners)) {
            userParticipated = match.winners.some(winner => {
              const winnerId = typeof winner === 'string' ? winner : winner?._id || winner?.id;
              return winnerId === user._id;
            });
          }
          
          // Check if user is the creator
          if (!userParticipated && match.creator) {
            const creatorId = typeof match.creator === 'string' ? match.creator : match.creator?._id || match.creator?.id;
            userParticipated = creatorId === user._id;
          }
          
          return userParticipated;
        });
        
        console.log('User matches found:', userMatches.length);
        
        // Sort by completion date (newest first)
        userMatches.sort((a, b) => {
          const dateA = new Date(a.completedAt || a.updatedAt || a.createdAt);
          const dateB = new Date(b.completedAt || b.updatedAt || b.createdAt);
          return dateB - dateA;
        });
        
        // Calculate statistics
        const totalMatches = userMatches.length;
        let wins = 0;
        let totalPrizeWon = 0;
        
        userMatches.forEach(match => {
          if (didUserWin(match)) {
            wins++;
            const winnersCount = match.winners?.length || 1;
            const prizeWon = Math.floor((match.prizePool || 0) / winnersCount);
            totalPrizeWon += prizeWon;
          }
        });
        
        const losses = totalMatches - wins;
        const winRate = totalMatches > 0 ? Math.round((wins / totalMatches) * 100) : 0;
        const averagePrize = wins > 0 ? Math.round(totalPrizeWon / wins) : 0;
        
        setStats({
          totalMatches,
          wins,
          losses,
          winRate,
          totalPrizeWon,
          averagePrize
        });
        
        setMatchHistory(userMatches);
        console.log('Match history loaded successfully:', userMatches.length, 'matches');
        
      } catch (error) {
        console.error('Error loading match history:', error);
        setError(error.message || 'Failed to load match history');
        toast.error(t('failedToLoadMatchHistory', 'Failed to load match history'));
      } finally {
        setLoading(false);
      }
    };

    loadMatchHistory();
  }, [user?._id, t]);

  // Helper function to check if user won
  const didUserWin = (match) => {
    if (!match.winners || !Array.isArray(match.winners)) return false;
    
    return match.winners.some(winner => {
      const winnerId = typeof winner === 'string' ? winner : winner?._id || winner?.id;
      return winnerId === user._id;
    });
  };

  // Helper function to get display name
  const getUserDisplayName = (userObj) => {
    if (typeof userObj === 'string') return userObj;
    if (userObj && typeof userObj === 'object') {
      return userObj.inGameName || userObj.username || userObj.name || userObj._id || userObj.id;
    }
    return 'Unknown';
  };

  // Helper function to get all participants (including fillers)
  const getAllParticipants = (match) => {
    const participants = [];
    
    // Add real participants
    if (match.participants && Array.isArray(match.participants)) {
      participants.push(...match.participants.map(p => ({
        ...p,
        type: 'real',
        displayName: getUserDisplayName(p),
        isCurrentUser: (typeof p === 'string' ? p : p._id || p.id) === user._id,
        isWinner: match.winners?.some(w => {
          const winnerId = typeof w === 'string' ? w : w._id || w.id;
          const participantId = typeof p === 'string' ? p : p._id || p.id;
          return winnerId === participantId;
        })
      })));
    }
    
    // Add fillers if available
    if (match.fillers && Array.isArray(match.fillers)) {
      participants.push(...match.fillers.map(f => ({
        ...f,
        type: 'filler',
        displayName: f.name || 'Filler',
        isCurrentUser: false,
        isWinner: match.winners?.some(w => {
          const winnerId = typeof w === 'string' ? w : w._id || w.id;
          return winnerId === f._id;
        })
      })));
    }
    
    // Add creator if not already included
    if (match.creator) {
      const creatorId = typeof match.creator === 'string' ? match.creator : match.creator._id || match.creator.id;
      const alreadyIncluded = participants.some(p => {
        const pId = typeof p === 'string' ? p : p._id || p.id;
        return pId === creatorId;
      });
      if (!alreadyIncluded) {
        participants.push({
          ...match.creator,
          type: 'real',
          displayName: getUserDisplayName(match.creator),
          isCurrentUser: creatorId === user._id,
          isWinner: match.winners?.some(w => {
            const winnerId = typeof w === 'string' ? w : w._id || w.id;
            return winnerId === creatorId;
          })
        });
      }
    }
    
    return participants;
  };

  // Helper function to get time since
  const getTimeSince = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins}${t('mins', 'm')}`;
    if (diffHours < 24) return `${diffHours}${t('hrs', 'h')}`;
    return `${diffDays}${t('daysShort', 'd')}`;
  };

  // Calculate pagination
  const totalPages = Math.ceil(matchHistory.length / MATCHES_PER_PAGE);
  const startIndex = (currentPage - 1) * MATCHES_PER_PAGE;
  const currentMatches = matchHistory.slice(startIndex, startIndex + MATCHES_PER_PAGE);

  // Reset page if needed
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="content-section">
        <div className="card">
          <div className="loading-spinner-content">
            <div className="loading-spinner"></div>
            <p className="text-gray-400">{t('loadingMatchHistory', 'Loading match history...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-section">
        <div className="card">
          <div className="tournament-empty-state">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="tournament-empty-title text-red-400">{t('errorLoadingHistory', 'Error loading match history')}</p>
            <p className="tournament-empty-subtitle">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-3 tournament-btn-base tournament-btn-deploy"
            >
              {t('retry', 'Retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="tournament-list-header">
        <div className="tournament-list-header-content">
          <h2 className="tournament-list-title">
            <Trophy className="w-5 h-5 text-yellow-500" />
            {t('matchHistory', 'MATCH HISTORY')}
          </h2>
          
          <button 
            onClick={() => { 
              window.location.reload(); 
              toast.success(t('historyRefreshed', 'History refreshed!')); 
            }}
            className="tournament-refresh-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {t('refresh', 'REFRESH')}
          </button>
        </div>
      </div>

      {/* Main Container */}
      <div className="tournament-list-container">
        {/* Statistics */}
        <div className="tournament-list-content">
          <div className="mb-3 p-2 bg-gray-800/40 rounded border border-gray-700">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-blue-400 font-bold text-xs">
                <TrendingUp className="w-3 h-3" />
                {t('combatStats', 'STATS')}:
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="tournament-status-base" style={{ backgroundColor: 'rgba(107, 114, 128, 0.2)', borderColor: '#6b7280', color: '#d1d5db', minWidth: 'auto', padding: '2px 6px' }}>
                  {stats.totalMatches} {t('total', 'Total')}
                </div>
                <div className="tournament-status-base" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', borderColor: '#22c55e', color: '#86efac', minWidth: 'auto', padding: '2px 6px' }}>
                  {stats.wins} {t('wins', 'W')}
                </div>
                <div className="tournament-status-base" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', borderColor: '#ef4444', color: '#fca5a5', minWidth: 'auto', padding: '2px 6px' }}>
                  {stats.losses} {t('losses', 'L')}
                </div>
                <div className="tournament-status-base" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', borderColor: '#3b82f6', color: '#60a5fa', minWidth: 'auto', padding: '2px 6px' }}>
                  {stats.winRate}% {t('winRate', 'WR')}
                </div>
                <div className="tournament-status-base" style={{ backgroundColor: 'rgba(251, 191, 36, 0.2)', borderColor: '#fbbf24', color: '#fde68a', minWidth: 'auto', padding: '2px 6px' }}>
                  {stats.totalPrizeWon} {t('totalPrize', 'Prize')}
                </div>
                <div className="tournament-status-base" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', borderColor: '#8b5cf6', color: '#c4b5fd', minWidth: 'auto', padding: '2px 6px' }}>
                  {stats.averagePrize} {t('avgPrize', 'Avg')}
                </div>
              </div>
            </div>
          </div>

          {/* Match History */}
          {matchHistory.length === 0 ? (
            <div className="tournament-empty-state">
              <Trophy className="w-12 h-12 text-gray-600" />
              <p className="tournament-empty-title">{t('noCompletedMatches', 'NO COMPLETED MATCHES')}</p>
              <p className="tournament-empty-subtitle">{t('joinTournamentsHistory', 'Join tournaments to build your combat record!')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {currentMatches.map((match, index) => {
                  const isWinner = didUserWin(match);
                  const winnersCount = match.winners?.length || 1;
                  const prizeWon = isWinner ? Math.floor((match.prizePool || 0) / winnersCount) : 0;
                  const creatorName = getUserDisplayName(match.creator);
                  const matchDate = new Date(match.completedAt || match.updatedAt || match.createdAt);
                  const globalIndex = startIndex + index;
                  const allParticipants = getAllParticipants(match);
                  const isGiveaway = match.tournamentType === 'giveaway';
                  const isHighPrize = match.prizePool >= 500;
                  
                  // Get card classes based on match type
                  const getCardClasses = () => {
                    const baseClasses = ['tournament-card-compact', 'tournament-card-base'];
                    
                    if (isGiveaway) {
                      baseClasses.push('tournament-card-giveaway');
                    } else if (isHighPrize) {
                      baseClasses.push('tournament-card-high-prize');
                    } else {
                      baseClasses.push('tournament-card-standard');
                    }
                    
                    return baseClasses.join(' ');
                  };
                  
                  return (
                    <div key={match._id || index} className={getCardClasses()}>
                      {/* Giveaway Badge */}
                      {isGiveaway && (
                        <div className="tournament-giveaway-badge">
                          <span>🎁</span>
                          <span>{t('giveaway', 'GIVEAWAY')}</span>
                        </div>
                      )}
                      
                      {/* Border effects */}
                      {isGiveaway && (
                        <>
                          <div className="tournament-border-glow-top"></div>
                          <div className="tournament-border-glow-bottom"></div>
                        </>
                      )}
                      {isHighPrize && !isGiveaway && (
                        <>
                          <div className="tournament-border-red-top"></div>
                          <div className="tournament-border-red-bottom"></div>
                        </>
                      )}

                      <div className="tournament-card-compact-main">
                        <div className="tournament-card-compact-left">
                          <div className="tournament-card-compact-title" 
                               style={{ color: isGiveaway ? '#fef3c7' : isHighPrize ? '#fecaca' : 'white' }}>
                            {creatorName}'s {match.gameMode === 'multiplayer' ? 'MP' : 'BR'} #{globalIndex + 1}
                          </div>
                          
                          <div className="tournament-card-compact-details">
                            <span className="text-red-400">⚔️{getGameModeDisplay(match.gameMode)}</span>
                            <span className="text-blue-400">📍{match.map || 'Unknown'}</span>
                            <span className="text-yellow-400">👥{allParticipants.length}</span>
                            {match.gameMode === 'multiplayer' && match.maxKills && (
                              <span className="text-pink-400">💀{match.maxKills}K</span>
                            )}
                            <span className={`font-bold ${
                              isGiveaway ? 'text-yellow-400 animate-pulse' : 
                              isHighPrize ? 'text-red-400' : 'text-green-400'
                            }`}>
                              🏆{match.prizePool || 0}
                            </span>
                            <span className="tournament-card-compact-time">⏰{getTimeSince(match.completedAt || match.createdAt)}</span>
                          </div>
                        </div>

                        <div className="tournament-card-compact-right">
                          <div className="tournament-card-compact-actions">
                            {/* Match Result Status */}
                            <div className={`tournament-status-base ${
                              isWinner 
                                ? isGiveaway ? 'tournament-status-completed-giveaway' : 'tournament-status-completed'
                                : 'tournament-status-cancelled'
                            }`}>
                              {isWinner ? (
                                <>
                                  <Trophy className="w-3 h-3" />
                                  {t('won', 'WON')}
                                </>
                              ) : (
                                <>
                                  <Activity className="w-3 h-3" />
                                  {t('lost', 'LOST')}
                                </>
                              )}
                            </div>

                            {/* Prize Won */}
                            {isWinner && prizeWon > 0 && (
                              <div className="tournament-status-base" style={{
                                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                borderColor: '#22c55e',
                                color: '#86efac'
                              }}>
                                +{prizeWon}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="tournament-card-compact-bottom">
                        <div className="tournament-card-compact-team">
                          <span className="tournament-card-compact-team-label">{t('combatants', 'COMBATANTS')}:</span>
                          
                          <div className="flex flex-wrap gap-1 mt-1">
                            {allParticipants.map((participant, participantIndex) => {
                              const participantId = typeof participant === 'string' ? participant : participant._id || participant.id;
                              
                              return (
                                <span key={participantId || participantIndex} 
                                      className={`tournament-participant-badge ${
                                        participant.isCurrentUser 
                                          ? 'tournament-participant-self' 
                                          : participant.isWinner
                                          ? isGiveaway ? 'tournament-participant-winner' : 'tournament-participant-winner-high-prize'
                                          : participant.type === 'filler'
                                          ? 'tournament-participant-filler'
                                          : 'tournament-participant-other'
                                      }`}>
                                  {participant.type === 'filler' ? (
                                    <>🤖 {participant.displayName}</>
                                  ) : participant.isWinner ? (
                                    <>👑 {participant.displayName}</>
                                  ) : (
                                    <>👤 {participant.displayName}</>
                                  )}
                                  {participant.isCurrentUser && ` (${t('you', 'You')})`}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        <div className="tournament-card-compact-info">
                          {match.password && <span className="text-yellow-500 font-bold">{t('passwordProtected', '🔒PWD')}</span>}
                          {isHighPrize && !isGiveaway && <span className="text-red-500 font-bold">{t('highStakes', '🔥HIGH STAKES')}</span>}
                          <span className="text-gray-400 text-xs">📅{matchDate.toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Winners Section (if multiple winners) */}
                      {match.winners && match.winners.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-sm text-gray-400 mb-1">
                            {t('winners', 'Winners')} ({match.winners.length}):
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {match.winners.map((winner, winnerIndex) => {
                              const winnerName = getUserDisplayName(winner);
                              const winnerId = typeof winner === 'string' ? winner : winner._id || winner.id;
                              const isCurrentUser = winnerId === user._id;
                              
                              return (
                                <span key={winnerId || winnerIndex} 
                                      className={`tournament-participant-badge tournament-participant-winner`}>
                                  <Trophy className="w-3 h-3" />
                                  {winnerName}{isCurrentUser && ` (${t('you', 'You')})`}
                                </span>
                              );
                            })}
                          </div>
                          {winnersCount > 1 && isWinner && (
                            <div className="text-xs text-gray-400 mt-1">
                              {t('prizeShared', 'Prize shared between {count} winners').replace('{count}', winnersCount)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination using existing tournament style */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-controls">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="pagination-button pagination-nav"
                    >
                      ← {t('prev', 'Prev')}
                    </button>

                    <span className="pagination-info">
                      {currentPage} / {totalPages}
                    </span>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-button pagination-nav"
                    >
                      {t('next', 'Next')} →
                    </button>
                  </div>
                  
                  <div className="pagination-info">
                    {t('pageInfo', 'Showing {current} of {total} matches')
                      .replace('{current}', `${startIndex + 1}-${Math.min(startIndex + MATCHES_PER_PAGE, matchHistory.length)}`)
                      .replace('{total}', matchHistory.length)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchHistory;